/**
 * NATIVE AUTH CONTEXT v1.4
 *
 * v1.4 - LOGOUT FIX: Timeout + Browser.close() Fallback bei signOut
 * v1.3 - DEEP LINK FIX: Capacitor App Listener für Callback-Verarbeitung
 * v1.2 - AUTO MIGRATION: Löscht alte Sessions bei Breaking Changes
 * v1.1 - FIX: Prüft Auth-Status nach "cancelled" Error
 * v1.0 - Auth Context für native Apps mit @logto/capacitor
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import LogtoClient from '@logto/capacitor';
import { Preferences } from '@capacitor/preferences';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { REDIRECT_CONFIG } from '../index';

// ✅ v1.2: AUTH VERSION für Breaking Changes
const AUTH_VERSION = '1.4.0'; // Logout redirect fix
const AUTH_VERSION_KEY = 'planivio_auth_version';

// Logto Client Singleton
let logtoClient = null;

const getLogtoClient = () => {
  if (!logtoClient) {
    logtoClient = new LogtoClient({
      endpoint: process.env.REACT_APP_LOGTO_ENDPOINT,
      appId: process.env.REACT_APP_LOGTO_APP_ID,
      resources: [process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL],
      scopes: ['openid', 'profile', 'email', 'offline_access']
    });
    console.log('✅ NativeAuthContext: LogtoClient initialized with resources');
  }
  return logtoClient;
};

// ✅ v1.2: Prüfe und migriere Auth Version
const checkAuthVersion = async (client) => {
  try {
    const { value: storedVersion } = await Preferences.get({ key: AUTH_VERSION_KEY });
    
    if (!storedVersion) {
      console.log('🔄 NativeAuthContext: First time setup, saving version', AUTH_VERSION);
      await Preferences.set({ key: AUTH_VERSION_KEY, value: AUTH_VERSION });
      return true;
    }
    
    if (storedVersion !== AUTH_VERSION) {
      console.log(`🔄 NativeAuthContext: Auth version mismatch!`);
      console.log(`   Stored: ${storedVersion}, Current: ${AUTH_VERSION}`);
      console.log('🗑️ NativeAuthContext: Clearing old session...');
      
      try {
        await client.signOut();
      } catch (e) {
        console.log('⚠️ SignOut during migration failed (expected):', e.message);
      }
      
      await Preferences.set({ key: AUTH_VERSION_KEY, value: AUTH_VERSION });
      console.log('✅ NativeAuthContext: Migration complete');
      
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('❌ NativeAuthContext: Version check failed:', err);
    return true;
  }
};

// Context
const NativeAuthContext = createContext(null);

/**
 * NativeAuthProvider - Ersetzt LogtoProvider für native Apps
 */
export const NativeAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const client = getLogtoClient();
  const isSigningOut = useRef(false);

  // ✅ v1.3: Deep Link Listener für OAuth Callbacks
  // ✅ v1.4: Guard gegen Logout-Redirects (isSigningOut)
  useEffect(() => {
    console.log('🔗 NativeAuthContext: Setting up Deep Link listener...');

    const handleAppUrlOpen = async (event) => {
      const url = event.url;
      console.log('🔗 Deep Link received:', url.substring(0, 50) + '...');

      // ✅ v1.4: Skip during signOut - SDK handles logout redirect internally
      if (isSigningOut.current) {
        console.log('🔗 Ignoring deep link during signOut');
        return;
      }

      // Prüfe ob es ein OAuth Callback ist
      if (url && url.includes('://callback')) {
        console.log('🔐 OAuth Callback detected, processing...');

        try {
          await client.handleSignInCallback(url);

          const authenticated = await client.isAuthenticated();
          console.log('✅ Callback processed, authenticated:', authenticated);

          setIsAuthenticated(authenticated);

          if (authenticated) {
            console.log('🎉 User is now logged in!');
          }
        } catch (err) {
          console.error('❌ Error processing callback:', err.message);
        }
      }
    };

    const listener = CapacitorApp.addListener('appUrlOpen', handleAppUrlOpen);

    return () => {
      listener.remove();
    };
  }, [client]);

  // Auth-Status beim Start prüfen
  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('🔐 NativeAuthContext: Checking authentication...');
        
        const versionOk = await checkAuthVersion(client);
        
        if (!versionOk) {
          console.log('🔐 NativeAuthContext: Session migrated, user logged out');
          setIsAuthenticated(false);
          return;
        }
        
        const authenticated = await client.isAuthenticated();
        console.log('🔐 NativeAuthContext: isAuthenticated =', authenticated);
        setIsAuthenticated(authenticated);
      } catch (err) {
        console.error('❌ NativeAuthContext: Error checking auth:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [client]);

  // Sign In
  const signIn = useCallback(async (_redirectConfig) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('🔐 Starting signIn with redirect:', REDIRECT_CONFIG.redirectUri);

      await client.signIn(REDIRECT_CONFIG.redirectUri);

      const authenticated = await client.isAuthenticated();
      console.log('✅ SignIn completed, authenticated:', authenticated);
      setIsAuthenticated(authenticated);
      
    } catch (err) {
      console.log('⚠️ SignIn returned error:', err.message);
      
      // Prüfe trotzdem ob authentifiziert
      try {
        const authenticated = await client.isAuthenticated();
        
        if (authenticated) {
          console.log('✅ Login successful despite error');
          setIsAuthenticated(true);
          setError(null);
          return;
        }
      } catch (e) {
        // Ignorieren
      }
      
      if (!err.message?.includes('cancelled')) {
        console.error('❌ SignIn error:', err);
        setError(err);
        throw err;
      }
      
      // ✅ TIMING FIX: Warte auf Deep Link Callback
      console.log('⏳ Waiting for Deep Link callback...');
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Nochmal prüfen ob inzwischen eingeloggt
      try {
        const finalCheck = await client.isAuthenticated();
        
        if (finalCheck) {
          console.log('✅ Callback successful!');
          setIsAuthenticated(true);
          setError(null);
          return;
        }
      } catch (e) {
        // Ignorieren
      }
      
      console.log('ℹ️ User cancelled login');
      
    } finally {
      setIsLoading(false);
    }
  }, [client]);

  // Sign Out - ✅ v1.4: Timeout + Browser.close() Fallback
  const signOut = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      isSigningOut.current = true;
      console.log('🔐 NativeAuthContext: Starting signOut...');
      console.log('🔐 Post-logout URI:', REDIRECT_CONFIG.postLogoutRedirectUri);

      // SDK signOut opens in-app browser → Logto end session → redirect back
      // If Logto doesn't redirect (URI not registered), the Promise hangs forever
      // → Race with 10s timeout as fallback
      try {
        await Promise.race([
          client.signOut(REDIRECT_CONFIG.postLogoutRedirectUri),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('SignOut timeout after 10s')), 10000)
          )
        ]);
        console.log('✅ NativeAuthContext: SignOut completed via redirect');
      } catch (timeoutErr) {
        console.warn('⚠️ NativeAuthContext: SignOut timeout, forcing browser close...');
        try {
          await Browser.close();
        } catch (_) {
          // Browser might already be closed
        }
        console.log('✅ NativeAuthContext: SignOut completed via timeout fallback');
      }

      setIsAuthenticated(false);
    } catch (err) {
      console.error('❌ NativeAuthContext: SignOut error:', err);
      setIsAuthenticated(false);
      setError(err);
    } finally {
      isSigningOut.current = false;
      setIsLoading(false);
    }
  }, [client]);

  // Get Access Token
  const getAccessToken = useCallback(async (resource) => {
    try {
      return await client.getAccessToken(resource);
    } catch (err) {
      console.error('❌ NativeAuthContext: getAccessToken error:', err);
      throw err;
    }
  }, [client]);

  // Get ID Token Claims
  const getIdTokenClaims = useCallback(async () => {
    try {
      return await client.getIdTokenClaims();
    } catch (err) {
      console.error('❌ NativeAuthContext: getIdTokenClaims error:', err);
      throw err;
    }
  }, [client]);

  const value = {
    isAuthenticated,
    isLoading,
    error,
    signIn,
    signOut,
    getAccessToken,
    getIdTokenClaims,
    client,
  };

  return (
    <NativeAuthContext.Provider value={value}>
      {children}
    </NativeAuthContext.Provider>
  );
};

/**
 * useNativeAuth - Hook für native Auth
 */
export const useNativeAuth = () => {
  const context = useContext(NativeAuthContext);
  if (!context) {
    throw new Error('useNativeAuth must be used within a NativeAuthProvider');
  }
  return context;
};

export default NativeAuthContext;